import React, { useState, useEffect } from 'react';
import {
    IonCard,
    IonCardContent,
    IonCardTitle,
    IonCardSubtitle,
    IonCardHeader,
    IonSpinner,
    IonBadge,
} from '@ionic/react';

const STATION = { id: 'FDJFN', name: 'St. Catharines' };
const OBS_URL =
    'https://api.weather.gc.ca/collections/aqhi-observations-realtime/items' +
    `?f=json&lang=en&location_id=${STATION.id}&sortby=-observation_datetime&limit=1`;

// Air quality advisories are issued as ECCC weather alerts, scoped geographically.
// This bbox covers the Niagara region (the town and the St. Catharines AQHI station).
const AREA_BBOX = '-79.75,42.90,-79.15,43.35';
const ALERTS_URL =
    'https://api.weather.gc.ca/collections/weather-alerts/items' +
    `?f=json&lang=en&bbox=${AREA_BBOX}&limit=50`;

const ECCC_LINK = 'https://weather.gc.ca/airquality/pages/provincial_summary/on_e.html';

// Per-municipality bylaw wording. Lincoln is a Town.
// Verified against Town of Lincoln By-law 2017-110, s.44(h).
const BYLAW_SENTENCE =
    "Under the Town of Lincoln's Open Air Fire By-law (2017-110), open-air fires are " +
    "prohibited when a smog alert or air quality advisory has been issued by a provincial " +
    "or federal agency — regardless of the current Fire Risk Level. Contravention is " +
    "subject to an administrative penalty and cost recovery.";

// An ECCC air quality alert is "in effect" (→ burning prohibited) when it is visible,
// its status is not 'ended' (issued/continued are active), and its event hasn't ended.
const isAirQuality = (p) => /air quality|smog/i.test(p.alert_name_en || '');
const inEffect = (p) => {
    if (p.display_status !== 'visible') return false;
    if ((p.status_en || '').toLowerCase() === 'ended') return false;
    if (p.event_end_datetime && new Date(p.event_end_datetime) < new Date()) return false;
    return true;
};

const titleCase = (s) => (s || '').replace(/\b\w/g, (c) => c.toUpperCase());

const categorize = (aqhi) => {
    const n = Math.round(aqhi);
    if (n <= 3)
        return {
            label: 'Low Risk',
            color: 'success',
            advice: 'Ideal air quality for outdoor activities.',
        };
    if (n <= 6)
        return {
            label: 'Moderate Risk',
            color: 'warning',
            advice: 'Consider reducing strenuous outdoor activity if you experience symptoms.',
        };
    if (n <= 10)
        return {
            label: 'High Risk',
            color: 'danger',
            advice:
                'Reduce or reschedule strenuous outdoor activities. At-risk individuals should take it easy.',
        };
    return {
        label: 'Very High Risk',
        color: 'danger',
        advice: 'Avoid strenuous outdoor activity. At-risk individuals should stay indoors.',
    };
};

const AQHIWidget = () => {
    const [obs, setObs] = useState(null);
    const [failed, setFailed] = useState(false);
    const [advisory, setAdvisory] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetch(OBS_URL)
            .then((r) => r.json())
            .then((json) => {
                if (cancelled) return;
                const props =
                    json && json.features && json.features[0] && json.features[0].properties;
                if (props && typeof props.aqhi === 'number') {
                    setObs(props);
                } else {
                    setFailed(true);
                }
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        fetch(ALERTS_URL)
            .then((r) => r.json())
            .then((json) => {
                if (cancelled) return;
                const feats = (json && json.features) || [];
                const active = feats
                    .map((f) => f.properties)
                    .find((p) => isAirQuality(p) && inEffect(p));
                setAdvisory(active || null);
            })
            .catch(() => {
                // Best-effort: if the alerts feed is unavailable the banner just won't show.
                // The by-law applies regardless; this widget is an informational aid.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const cat = obs ? categorize(obs.aqhi) : null;

    return (
        <IonCard mode="ios">
            <IonCardHeader>
                <IonCardSubtitle>Air Quality</IonCardSubtitle>
                <IonCardTitle>
                    AQHI{' '}
                    {obs ? (
                        <>
                            {Math.round(obs.aqhi)}{' '}
                            <IonBadge color={cat.color}>{cat.label}</IonBadge>
                        </>
                    ) : null}
                </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
                {advisory ? (
                    <div
                        style={{
                            background: '#b3261e',
                            color: '#fff',
                            borderRadius: 8,
                            padding: '12px 14px',
                            marginBottom: 14,
                        }}
                    >
                        <div style={{ fontWeight: 700, fontSize: '1.05em', marginBottom: 6 }}>
                            🚫 Open-air burning is prohibited
                        </div>
                        <div style={{ fontSize: '0.9em', lineHeight: 1.45 }}>
                            An air quality advisory is in effect for our area. {BYLAW_SENTENCE}
                        </div>
                        <div style={{ fontSize: '0.75em', opacity: 0.85, marginTop: 6 }}>
                            {titleCase(advisory.alert_name_en) || 'Air Quality Warning'}
                            {advisory.event_end_datetime
                                ? ` · in effect until ${new Date(
                                      advisory.event_end_datetime,
                                  ).toLocaleString()}`
                                : ''}
                        </div>
                    </div>
                ) : null}

                {obs ? (
                    <>
                        <p>{cat.advice}</p>
                        <p style={{ fontSize: '0.8em', opacity: 0.7 }}>
                            {STATION.name} station &middot; {obs.observation_datetime_text_en}{' '}
                            &middot;{' '}
                            <a href={ECCC_LINK} target="_blank" rel="noopener noreferrer">
                                Forecast &amp; details
                            </a>
                        </p>
                    </>
                ) : failed ? (
                    <p>
                        Air quality data is temporarily unavailable.{' '}
                        <a href={ECCC_LINK} target="_blank" rel="noopener noreferrer">
                            Check current conditions at weather.gc.ca
                        </a>
                    </p>
                ) : (
                    <IonSpinner />
                )}
            </IonCardContent>
        </IonCard>
    );
};

export default AQHIWidget;
